import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';

import {
  EmptyState,
  Loading,
  PageTitle
} from 'components/common/Elements/Elements';
import { useRegion } from 'contexts/RegionContext';
import { getRequest } from 'utils/requests';

import AssignedCustomers from './AssignedCustomers';

interface FeatureRecord {
  _id: string;
  key: string;
  name: string;
  customerIdsCount?: number;
  [field: string]: unknown;
}

interface AssignedCustomersState {
  dataSelectCell?: FeatureRecord;
  countAssignedCustomers?: number;
  region?: string;
}

interface FeatureByKeyResponse {
  body: { feature: FeatureRecord | null };
}

type ResolveStatus = 'ready' | 'resolving' | 'notFound' | 'error' | 'missing';

/**
 * Route entry for the FF Assigned/Not-Assigned page. The flag record arrives in
 * router state from Feature List, tagged with the region it was read in. A flag
 * `_id` is only valid in its own region, so when the page is shown in another
 * one (the DR switch keeps this page, see REGION_SWITCH_STAY_PATHS) the flag is
 * re-fetched by `key` before the page mounts.
 */
const AssignedCustomersRoute = () => {
  const { t } = useTranslation(['activity']);
  const history = useHistory();
  const location = useLocation<AssignedCustomersState | undefined>();
  const { region } = useRegion();
  const unmount = useRef(false);

  const initialState = location.state;
  const stateRecord = initialState?.dataSelectCell;
  const stateIsCurrent = !!stateRecord && initialState?.region === region.code;

  const [flag, setFlag] = useState<FeatureRecord | undefined>(
    stateIsCurrent ? stateRecord : undefined
  );
  const [assignedCount, setAssignedCount] = useState<number | undefined>(
    stateIsCurrent ? initialState?.countAssignedCustomers : undefined
  );
  const [status, setStatus] = useState<ResolveStatus>(() => {
    if (stateIsCurrent) return 'ready';
    return stateRecord?.key ? 'resolving' : 'missing';
  });

  useEffect(() => {
    if (status === 'missing') {
      history.replace('/product/feature-flag');
      return;
    }
    if (status !== 'resolving' || !stateRecord) return;

    getRequest(
      `/product/fflag/by-key?key=${encodeURIComponent(stateRecord.key)}`
    )
      .then((res: FeatureByKeyResponse) => {
        if (unmount.current) return;
        const { feature } = res.body;
        if (!feature) {
          setStatus('notFound');
          return;
        }
        // Store the resolved record so back, refresh and a later switch start
        // from this region's copy.
        const { pathname, search } = history.location;
        history.replace(`${pathname}${search}`, {
          dataSelectCell: omit(feature, 'customerIds'),
          countAssignedCustomers: feature.customerIdsCount,
          region: region.code
        });
        setFlag(feature);
        setAssignedCount(feature.customerIdsCount);
        setStatus('ready');
      })
      .catch(() => {
        if (!unmount.current) setStatus('error');
      });
    // Runs once per mount; the region switch remounts this route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  if (status === 'ready' && flag) {
    return (
      <AssignedCustomers
        dataSelectCell={flag}
        isNotAssignedCustomers={assignedCount === 0}
      />
    );
  }

  if (status === 'notFound' && stateRecord) {
    return (
      <>
        <PageTitle title={stateRecord.name} />
        <EmptyState
          message={t('activity:featureFlag.NOT_AVAILABLE_IN_REGION', {
            region: region.fullName
          })}
        />
      </>
    );
  }

  if (status === 'error') {
    return <EmptyState message="connect" />;
  }

  return <Loading />;
};

export default AssignedCustomersRoute;
